import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Paper,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ColorLens as ColorLensIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
  Javascript as JavaScriptIcon,
  Html as HtmlIcon,
  Css as CssIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import ThemePreview from '../ThemePreview';

interface ThemeSettingsProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`theme-settings-tabpanel-${index}`}
      aria-labelledby={`theme-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// واجهة الثيم
interface Theme {
  id: string;
  name: string;
  description?: string;
  previewImage?: string;
  cssCode?: string;
  jsCode?: string;
  htmlCode?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const defaultThemes: Theme[] = [
  {
    id: 'default',
    name: 'الثيم الافتراضي',
    description: 'الثيم الافتراضي للمتجر - أزرق كلاسيكي',
    previewImage: 'https://via.placeholder.com/400x200/0d51d6/ffffff?text=الثيم+الافتراضي',
    cssCode: '',
    jsCode: '',
    htmlCode: '',
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'dark',
    name: 'الوضع الداكن',
    description: 'ثيم داكن مريح للعينين',
    previewImage: 'https://via.placeholder.com/400x200/121212/ffffff?text=الوضع+الداكن',
    cssCode: 'body { background-color: #121212; color: #ffffff; }',
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'green',
    name: 'الثيم الأخضر',
    description: 'ثيم بألوان خضراء طبيعية',
    previewImage: 'https://via.placeholder.com/400x200/4caf50/ffffff?text=الثيم+الأخضر',
    cssCode: ':root { --primary-color: #4caf50; --secondary-color: #2e7d32; }',
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'modern_blue',
    name: 'الأزرق العصري',
    description: 'ثيم أزرق عصري مع تدرجات جميلة',
    previewImage: 'https://via.placeholder.com/400x200/1976d2/ffffff?text=الأزرق+العصري',
    cssCode: '',
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'elegant_pink',
    name: 'الوردي الأنيق',
    description: 'ثيم وردي أنيق ومميز',
    previewImage: 'https://via.placeholder.com/400x200/e91e63/ffffff?text=الوردي+الأنيق',
    cssCode: '',
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'noon',
    name: 'ثيم أصفر (مستوحى من نون)',
    description: 'ثيم بألوان صفراء حيوية مثل منصة نون',
    previewImage: 'https://via.placeholder.com/400x200/fee000/000000?text=ثيم+نون',
    cssCode: `
      :root {
        --primary-color: #fee000;
        --secondary-color: #383838;
        --background-color: #f7f7fa;
        --text-color: #404553;
        --card-background: #ffffff;
        --border-color: #eaeaea;
        --header-background: #fee000;
        --header-text: #404553;
        --footer-background: #ffffff;
        --footer-text: #404553;
        --card-shadow: 0 1px 4px rgba(0,0,0,0.08);
      }

      body {
        background-color: var(--background-color) !important;
        color: var(--text-color) !important;
        font-family: 'Cairo', sans-serif;
      }

      .store-header {
        background-color: var(--header-background) !important;
        color: var(--header-text) !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
      }

      .store-footer {
        background-color: var(--footer-background) !important;
        color: var(--footer-text) !important;
        border-top: 1px solid var(--border-color) !important;
      }

      .product-card {
        background-color: var(--card-background) !important;
        border: 1px solid var(--border-color) !important;
        border-radius: 8px !important;
        transition: transform 0.2s ease, box-shadow 0.2s ease !important;
        box-shadow: var(--card-shadow) !important;
      }

      .product-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important;
        border-color: #cccccc !important;
      }

      .MuiButton-containedPrimary {
        background-color: var(--primary-color) !important;
        color: #404553 !important;
        font-weight: 700 !important;
        border-radius: 4px !important;
        box-shadow: none !important;
      }

      .MuiButton-containedPrimary:hover {
        background-color: #f3d600 !important;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1) !important;
      }
    `,
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'amazon',
    name: 'ثيم كلاسيكي (مستوحى من أمازون)',
    description: 'ثيم بألوان داكنة وبرتقالية مثل أمازون',
    previewImage: 'https://via.placeholder.com/400x200/232f3e/ff9900?text=ثيم+أمازون',
    cssCode: `
      :root {
        --primary-color: #131921;
        --secondary-color: #febd69;
        --background-color: #eaeded;
        --text-color: #0f1111;
        --card-background: #ffffff;
        --border-color: #d5d9d9;
        --header-background: #131921;
        --header-text: #ffffff;
        --footer-background: #232f3e;
        --footer-text: #ffffff;
        --card-shadow: 0 1px 3px rgba(0,0,0,0.05);
      }

      body {
        background-color: var(--background-color) !important;
        color: var(--text-color) !important;
        font-family: 'Cairo', Arial, sans-serif;
      }

      .store-header {
        background-color: var(--header-background) !important;
        color: var(--header-text) !important;
      }

      .store-footer {
        background-color: var(--footer-background) !important;
        color: var(--footer-text) !important;
      }

      .product-card {
        background-color: var(--card-background) !important;
        border-radius: 4px !important;
        border: 1px solid var(--border-color) !important;
        box-shadow: none !important;
        transition: none !important;
      }

      .product-card:hover {
        border-color: transparent !important;
        box-shadow: 0 0 0 1px #007185, 0 4px 12px rgba(0,0,0,0.15) !important;
      }

      .MuiButton-containedPrimary {
        background-color: #ffd814 !important;
        color: #0f1111 !important;
        border-radius: 100px !important;
        font-weight: 600 !important;
        border: 1px solid #fcd200 !important;
        box-shadow: 0 2px 5px rgba(213,217,217,0.5) !important;
      }

      .MuiButton-containedPrimary:hover {
        background-color: #f7ca00 !important;
        border-color: #f2c200 !important;
      }
    `,
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'alibaba',
    name: 'ثيم برتقالي (مستوحى من علي بابا)',
    description: 'ثيم برتقالي قوي مثل منصة علي بابا',
    previewImage: 'https://via.placeholder.com/400x200/ff6a00/ffffff?text=ثيم+علي+بابا',
    cssCode: `
      :root {
        --primary-color: #ff6a00;
        --secondary-color: #ff9000;
        --background-color: #f2f3f5;
        --text-color: #333333;
        --card-background: #ffffff;
        --border-color: transparent;
        --header-background: #ffffff;
        --header-text: #333333;
        --footer-background: #ffffff;
        --footer-text: #666666;
        --card-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      body {
        background-color: var(--background-color) !important;
        color: var(--text-color) !important;
        font-family: 'Cairo', sans-serif;
      }

      .store-header {
        background-color: var(--header-background) !important;
        color: var(--header-text) !important;
        border-bottom: 2px solid var(--primary-color) !important;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05) !important;
      }

      .store-footer {
        background-color: var(--footer-background) !important;
        color: var(--footer-text) !important;
        border-top: 1px solid #e5e5e5 !important;
      }

      .product-card {
        background-color: var(--card-background) !important;
        border-radius: 12px !important;
        border: none !important;
        box-shadow: var(--card-shadow) !important;
        transition: box-shadow 0.3s ease, border 0.3s ease !important;
      }

      .product-card:hover {
        box-shadow: 0 6px 18px rgba(255, 106, 0, 0.15) !important;
      }

      .MuiButton-containedPrimary {
        background: linear-gradient(90deg, #ff9000 0%, #ff5000 100%) !important;
        color: #ffffff !important;
        border-radius: 20px !important;
        font-weight: bold !important;
        border: none !important;
        box-shadow: 0 3px 6px rgba(255, 106, 0, 0.3) !important;
      }

      .MuiButton-containedPrimary:hover {
        background: linear-gradient(90deg, #ff7b00 0%, #e64800 100%) !important;
        box-shadow: 0 4px 8px rgba(255, 106, 0, 0.4) !important;
      }
    `,
    jsCode: '',
    htmlCode: '',
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

const ThemeSettings: React.FC<ThemeSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [themes, setThemes] = useState<Theme[]>(defaultThemes);

  // حالة مربع حوار الثيم
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  const [isNewTheme, setIsNewTheme] = useState(false);
  const [codeTab, setCodeTab] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTheme, setPreviewTheme] = useState<{ id: string; name: string } | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCodeTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCodeTab(newValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('display', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات العرض');
    } finally {
      onSave(false);
    }
  };

  // وظائف الثيم
  const handleOpenThemeDialog = (theme?: Theme) => {
    if (theme) {
      setCurrentTheme(theme);
      setIsNewTheme(false);
    } else {
      setCurrentTheme({
        id: `theme-${Date.now()}`,
        name: 'ثيم جديد',
        description: 'وصف الثيم الجديد',
        previewImage: 'https://via.placeholder.com/400x200?text=New+Theme',
        cssCode: '',
        jsCode: '',
        htmlCode: '',
        isActive: false,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      setIsNewTheme(true);
    }
    setThemeDialogOpen(true);
  };

  const handleCloseThemeDialog = () => {
    setThemeDialogOpen(false);
    setCurrentTheme(null);
    setCodeTab(0);
  };

  const handleSaveTheme = () => {
    if (!currentTheme) return;

    try {
      onSave(true);

      let updatedThemes: Theme[];

      if (isNewTheme) {
        updatedThemes = [...themes, currentTheme];
      } else {
        updatedThemes = themes.map(theme =>
          theme.id === currentTheme.id ? currentTheme : theme
        );
      }

      // إذا كان الثيم الجديد مفعل، قم بتعطيل جميع الثيمات الأخرى
      if (currentTheme.isActive) {
        updatedThemes = updatedThemes.map(theme => ({
          ...theme,
          isActive: theme.id === currentTheme.id
        }));
      }

      setThemes(updatedThemes);

      // تحديث الثيم في إعدادات العرض
      setFormData(prev => ({
        ...prev,
        theme: currentTheme.isActive ? currentTheme.id : prev.theme
      }));

      // تسجيل الثيم في ThemeProvider
      if (typeof window !== 'undefined' && (window as any).registerStoreTheme) {
        (window as any).registerStoreTheme({
          id: currentTheme.id,
          name: currentTheme.name,
          cssCode: currentTheme.cssCode,
          jsCode: currentTheme.jsCode,
          htmlCode: currentTheme.htmlCode
        });

        console.log('Theme registered with ThemeProvider:', currentTheme.id);
      }

      handleCloseThemeDialog();
    } catch (error) {
      onError('حدث خطأ أثناء حفظ الثيم');
    } finally {
      onSave(false);
    }
  };

  const handleDeleteTheme = (themeId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الثيم؟')) {
      try {
        onSave(true);

        // لا يمكن حذف الثيم الافتراضي
        if (themeId === 'default') {
          alert('لا يمكن حذف الثيم الافتراضي');
          return;
        }

        const updatedThemes = themes.filter(theme => theme.id !== themeId);
        setThemes(updatedThemes);

        // إذا كان الثيم المحذوف هو المفعل، قم بتفعيل الثيم الافتراضي
        if (formData.theme === themeId) {
          setFormData(prev => ({ ...prev, theme: 'default' }));
        }
      } catch (error) {
        onError('حدث خطأ أثناء حذف الثيم');
      } finally {
        onSave(false);
      }
    }
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!currentTheme) return;

    const { name, value } = e.target;
    setCurrentTheme(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleActivateTheme = async (themeId: string) => {
    try {
      onSave(true);

      // تفعيل الثيم المحدد وتعطيل الباقي
      const updatedThemes = themes.map(theme => ({
        ...theme,
        isActive: theme.id === themeId
      }));

      setThemes(updatedThemes);

      // تحديث الثيم في إعدادات العرض
      const updatedFormData = { ...formData, theme: themeId };
      setFormData(updatedFormData);

      // حفظ الإعدادات فوراً
      await updateSection('display', updatedFormData);

      // تطبيق الثيم فوراً
      if (typeof window !== 'undefined') {
        // إرسال حدث لتطبيق الثيم
        window.dispatchEvent(new CustomEvent('themeChanged', {
          detail: { themeId }
        }));

        // تطبيق الثيم مباشرة إذا كان متاحاً
        if ((window as any).applyStoreTheme) {
          (window as any).applyStoreTheme(themeId);
        }
      }

      onSuccess(true);
      console.log('Theme activated successfully:', themeId);
    } catch (error) {
      console.error('Error activating theme:', error);
      onError('حدث خطأ أثناء تفعيل الثيم');
    } finally {
      onSave(false);
    }
  };

  // وظائف معاينة الثيم
  const handlePreviewTheme = (theme: Theme) => {
    setPreviewTheme({ id: theme.id, name: theme.name });
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTheme(null);
  };

  const handleApplyFromPreview = async (themeId: string) => {
    await handleActivateTheme(themeId);
    handleClosePreview();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات الثيمات
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              console.log('Edit button clicked, setting editing to true');
              setEditing(true);
            }}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      {/* زر إضافي لتفعيل التحرير مباشرة */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => {
            console.log('Force edit button clicked');
            setEditing(true);
          }}
        >
          انقر هنا لتفعيل التحرير مباشرة
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="theme settings tabs"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="الثيمات" />
        <Tab label="إعدادات العرض" />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            الثيمات المتاحة ({themes.length})
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              startIcon={<VisibilityIcon />}
              variant="outlined"
              color="secondary"
              component={Link}
              to="/store/theme-demo"
            >
              معاينة الثيم
            </Button>

            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="primary"
              onClick={() => handleOpenThemeDialog()}
              disabled={!editing}
            >
              إضافة ثيم جديد
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {themes.map((theme) => (
            <Grid item xs={12} md={4} key={theme.id}>
              <Card sx={{ height: '100%' }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={theme.previewImage || 'https://via.placeholder.com/400x200?text=Theme'}
                  alt={theme.name}
                />
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {theme.name}
                  </Typography>
                  {theme.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {theme.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" color={theme.isActive ? 'success.main' : 'text.secondary'}>
                      {theme.isActive ? 'مفعل' : 'غير مفعل'}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreviewTheme(theme)}
                    variant="outlined"
                    color="info"
                  >
                    معاينة
                  </Button>
                  {!theme.isActive && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => handleActivateTheme(theme.id)}
                      disabled={!editing}
                      variant="contained"
                    >
                      تفعيل
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenThemeDialog(theme)}
                    disabled={!editing}
                  >
                    تعديل
                  </Button>
                  {theme.id !== 'default' && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteTheme(theme.id)}
                      disabled={!editing}
                    >
                      حذف
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="عدد المنتجات في الصفحة"
              name="productsPerPage"
              type="number"
              value={formData.productsPerPage}
              onChange={handleChange}
              disabled={!editing}
              InputProps={{ inputProps: { min: 4, max: 48 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showOutOfStockProducts}
                  onChange={handleChange}
                  name="showOutOfStockProducts"
                  disabled={!editing}
                />
              }
              label="عرض المنتجات غير المتوفرة"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showProductRatings}
                  onChange={handleChange}
                  name="showProductRatings"
                  disabled={!editing}
                />
              }
              label="عرض تقييمات المنتجات"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showRelatedProducts}
                  onChange={handleChange}
                  name="showRelatedProducts"
                  disabled={!editing}
                />
              }
              label="عرض المنتجات ذات الصلة"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableWishlist}
                  onChange={handleChange}
                  name="enableWishlist"
                  disabled={!editing}
                />
              }
              label="تفعيل قائمة الرغبات"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableProductComparison}
                  onChange={handleChange}
                  name="enableProductComparison"
                  disabled={!editing}
                />
              }
              label="تفعيل مقارنة المنتجات"
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* مربع حوار إضافة/تعديل الثيم */}
      <Dialog
        open={themeDialogOpen}
        onClose={handleCloseThemeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNewTheme ? 'إضافة ثيم جديد' : 'تعديل الثيم'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم الثيم"
                name="name"
                value={currentTheme?.name || ''}
                onChange={handleThemeChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رابط صورة المعاينة"
                name="previewImage"
                value={currentTheme?.previewImage || ''}
                onChange={handleThemeChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="وصف الثيم"
                name="description"
                value={currentTheme?.description || ''}
                onChange={handleThemeChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentTheme?.isActive || false}
                    onChange={(e) => {
                      if (currentTheme) {
                        setCurrentTheme({
                          ...currentTheme,
                          isActive: e.target.checked
                        });
                      }
                    }}
                  />
                }
                label="تفعيل الثيم"
              />
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={codeTab}
                onChange={handleCodeTabChange}
                aria-label="code tabs"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="CSS" icon={<CssIcon />} />
                <Tab label="JavaScript" icon={<JavaScriptIcon />} />
                <Tab label="HTML" icon={<HtmlIcon />} />
              </Tabs>
              <Box sx={{ p: 2, border: '1px solid #ddd', borderTop: 0 }}>
                {codeTab === 0 && (
                  <TextField
                    fullWidth
                    label="كود CSS"
                    name="cssCode"
                    value={currentTheme?.cssCode || ''}
                    onChange={handleThemeChange}
                    multiline
                    rows={10}
                    placeholder="/* أدخل كود CSS هنا */"
                  />
                )}
                {codeTab === 1 && (
                  <TextField
                    fullWidth
                    label="كود JavaScript"
                    name="jsCode"
                    value={currentTheme?.jsCode || ''}
                    onChange={handleThemeChange}
                    multiline
                    rows={10}
                    placeholder="// أدخل كود JavaScript هنا"
                  />
                )}
                {codeTab === 2 && (
                  <TextField
                    fullWidth
                    label="كود HTML"
                    name="htmlCode"
                    value={currentTheme?.htmlCode || ''}
                    onChange={handleThemeChange}
                    multiline
                    rows={10}
                    placeholder="<!-- أدخل كود HTML هنا -->"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseThemeDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveTheme} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditing(false)}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}

      {/* مكون معاينة الثيم */}
      {previewTheme && (
        <ThemePreview
          themeId={previewTheme.id}
          themeName={previewTheme.name}
          open={previewOpen}
          onClose={handleClosePreview}
          onApply={handleApplyFromPreview}
        />
      )}
    </Box>
  );
};

export default ThemeSettings;
